/**
 * TrendIndicator Component
 *
 * Reusable component for displaying trend direction with icon and percentage.
 */

import React from "react";
import type { TrendDirection } from "@/types/security";
import { TREND_CONFIG } from "@/constants/trends";

interface TrendIndicatorProps {
  direction: TrendDirection;
  percentage: number;
  size?: "sm" | "md" | "lg";
  showSign?: boolean;
}

const SIZE_CONFIG = {
  sm: { icon: "h-3.5 w-3.5", text: "text-sm" },
  md: { icon: "h-4 w-4", text: "text-base" },
  lg: { icon: "h-5 w-5", text: "text-2xl" },
} as const;

export const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  direction,
  percentage,
  size = "sm",
  showSign = false,
}) => {
  const { Icon, color } = TREND_CONFIG[direction];
  const { icon: iconSize, text: textSize } = SIZE_CONFIG[size];

  const formattedPercentage = showSign
    ? direction === "up"
      ? `+${Math.abs(percentage)}%`
      : direction === "down"
        ? `-${Math.abs(percentage)}%`
        : `${Math.abs(percentage)}%`
    : `${Math.abs(percentage)}%`;

  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`${iconSize} ${color}`} />
      <span className={`${textSize} font-medium ${color}`}>{formattedPercentage}</span>
    </div>
  );
};
